import {
  createEntityAdapter,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import dayjs from 'src/libs/dayjs';

import { RootState } from 'src/store/index';
import { selector as replySelector } from 'src/store/model/discussionReply';
import { Pagination } from 'src/interfaces/general';

export type State = {
  loading: boolean;
  pagination: Pagination;
};

export const adapter = createEntityAdapter<Model.Discussion>({
  selectId: (model) => model.id,
  sortComparer: (a, b) => {
    return dayjs(a.created_at).isAfter(b.created_at) ? -1 : 1;
  },
});

const { reducer, actions } = createSlice({
  name: 'discussion',
  initialState: adapter.getInitialState<State>({
    loading: false,
    pagination: {
      current_page: 0,
      item_from: 0,
      item_to: 0,
      limit: 0,
      next_page: 1,
      previous_page: 0,
      total_pages: 0,
      total_items: 0,
    },
  }),
  reducers: {
    updateEntities(state, action: PayloadAction<Model.Discussion[]>) {
      adapter.upsertMany(state, action);
    },
    updateEntity(state, action: PayloadAction<Model.Discussion>) {
      adapter.upsertOne(state, action);
    },
    updateError(state, action: PayloadAction<Model.Discussion>) {
      adapter.updateOne(state, {
        id: action.payload?.id || 'placeholder',
        changes: {
          error: true,
          message: action.payload?.message,
        },
      });
    },
    removeEntity(state, action: PayloadAction<Model.Discussion>) {
      adapter.removeOne(state, action.payload.id);
    },
    updateLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    createDummy(state, action: PayloadAction<Partial<Model.Discussion>>) {
      const comment = {
        ...action.payload,
        id: 'placeholder',
      } as Model.Discussion;

      adapter.upsertOne(state, comment);
    },
    createEntity(state, action: PayloadAction<Model.Discussion>) {
      adapter.removeOne(state, 'placeholder');
      adapter.upsertOne(state, action);
    },
    updatePagination(state, action: PayloadAction<Pagination>) {
      state.pagination = action.payload;
    },
  },
});

export const selector = adapter.getSelectors(
  (state: RootState) => state.model.discussion,
);

export const selectDiscussionByContentId = createSelector(
  selector.selectAll,
  replySelector.selectAll,
  (state: RootState, class_topic_content_id?: string) => class_topic_content_id,
  (discussions, replies, class_topic_content_id) => {
    return discussions
      .filter((d) => d.class_topic_content_id === class_topic_content_id)
      .map((d) => {
        return {
          ...d,
          replies: replies.filter((r) => r.parent_id === d.id),
        };
      });
  },
);

export { reducer };
export default actions;
